import {
  getLocationFromLocalStorage,
  setLocationToLocalStorage,
} from "@/app/utils/services/service/localStorageService";
import type { FlightPlaces, FlightSearchRequest } from "@/types/flights";
import axios from "axios";
import { isEmpty } from "lodash";
import { useLocale, useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { ArrowLeftRight } from "../../Icons";
import { DestinationDropdown } from "./DestinationDropdown";

type Props = {
  from: string;
  to: string;
  withSwitch?: boolean;
  updateFlightField: (
    flightId: string,
    updatedFields: Partial<FlightSearchRequest>
  ) => void;
  flightId: string;
  isMultiCity?: boolean;
  index?: number;
  toLocation?: string;
};

const DestinationField = ({
  from,
  to,
  withSwitch = false,
  updateFlightField,
  flightId,
  isMultiCity,
  index,
  toLocation,
}: Props) => {
  const locale = useLocale();
  const t = useTranslations();
  const [searchValueFrom, setSearchValueFrom] = useState("");
  const [searchValueTo, setSearchValueTo] = useState(toLocation || "");
  const [optionsFrom, setOptionsFrom] = useState<FlightPlaces[]>([]);
  const [optionsTo, setOptionsTo] = useState<FlightPlaces[]>([]);
  const [selectedValueTo, setSelectedValueTo] = useState<FlightPlaces | null>(
    null
  );
  const [selectedValueFrom, setSelectedValueFrom] =
    useState<FlightPlaces | null>(null);

  const fetchDataFrom = async (queryParams: string) => {
    try {
      const res = await axios.get(
        `/api/flights/places?query=${queryParams}&lang=${locale}`
      );

      const ops = res?.data?.flights.slice(0, 5).map((flight: any) => ({
        name: flight?.name,
        iataCode: flight?.iataCode,
        city: flight?.city,
        timeZone: flight?.timeZone,
        type: flight?.type,
      }));

      setOptionsFrom([...ops]);
    } catch (er) {}
  };

  const fetchDataTo = async (queryParams: string) => {
    try {
      const res = await axios.get(
        `/api/flights/places?query=${queryParams}&lang=${locale}`
      );

      const ops = res?.data?.flights.slice(0, 5).map((flight: any) => ({
        name: flight?.name,
        iataCode: flight?.iataCode,
        city: flight?.city,
        timeZone: flight?.timeZone,
        type: flight?.type,
      }));

      setOptionsTo([...ops]);
    } catch (er) {}
  };

  useEffect(() => {
    const saved = getLocationFromLocalStorage(index?.toString() ?? "", locale);

    if (saved && !toLocation) {
      setSearchValueFrom(saved.from.name);
      setSelectedValueFrom(saved.from);
      updateFlightField(flightId, { from: saved.from.iataCode });

      setSearchValueTo(saved.to.name);
      setSelectedValueTo(saved.to);
      updateFlightField(flightId, { to: saved.to.iataCode });
    }
  }, []);

  useEffect(() => {
    let getData: ReturnType<typeof setTimeout>;

    if (isEmpty(searchValueFrom)) {
      setOptionsFrom([]);
      return undefined;
    }

    if (searchValueFrom && searchValueFrom != selectedValueFrom?.name) {
      getData = setTimeout(() => {
        fetchDataFrom(searchValueFrom);
      }, 500);
    }

    return () => {
      getData && clearTimeout(getData);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValueFrom]);

  useEffect(() => {
    let getData: ReturnType<typeof setTimeout>;

    if (isEmpty(searchValueTo)) {
      setOptionsTo([]);
      return undefined;
    }

    if (searchValueTo && searchValueTo != selectedValueTo?.name) {
      getData = setTimeout(() => {
        fetchDataTo(searchValueTo);
      }, 500);
    }

    return () => {
      getData && clearTimeout(getData);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValueTo]);

  const handleClickTo = ({ name, iataCode }: FlightPlaces) => {
    setSelectedValueTo({ name, iataCode });
    updateFlightField(flightId, { to: iataCode });
    setSearchValueTo(name);
    if (selectedValueFrom) {
      setLocationToLocalStorage(
        index?.toString() ?? "",
        selectedValueFrom,
        {
          name,
          iataCode,
        },
        locale
      );
    }
  };

  const handleClickFrom = ({ name, iataCode }: FlightPlaces) => {
    setSelectedValueFrom({ name, iataCode });
    updateFlightField(flightId, { from: iataCode });
    setSearchValueFrom(name);
    if (selectedValueTo) {
      setLocationToLocalStorage(
        flightId,
        selectedValueFrom,
        {
          name,
          iataCode,
        },
        locale
      );
    }
  };

  const handleSwitch = () => {
    handleClickFrom(selectedValueTo!);
    handleClickTo(selectedValueFrom!);
  };

  const onClearFrom = () => {
    setSelectedValueFrom(null);
    updateFlightField(flightId, { from: "" });
    setSearchValueFrom("");
  };

  const onClearTo = () => {
    setSelectedValueTo(null);
    updateFlightField(flightId, { to: "" });
    setSearchValueTo("");
  };

  const handleChangeFrom = (value: string) => {
    setSearchValueFrom(value);

    if (isEmpty(value)) {
      onClearFrom();
    }
  };

  const handleChangeTo = (value: string) => {
    setSearchValueTo(value);

    if (isEmpty(value)) {
      onClearTo();
    }
  };

  return (
    <div className="flex flex-col gap-2 sm:gap-0 sm:flex-row sm:border sm:border-sleap-gray-400 rounded-full relative sm:items-center">
      <DestinationDropdown
        onChange={handleChangeFrom}
        onClick={handleClickFrom}
        options={optionsFrom}
        value={searchValueFrom}
        otherValue={searchValueTo}
        label={t("HomePageFlights.from")}
        isMultiCity={isMultiCity}
        onClear={onClearFrom}
      />
      {withSwitch && (
        <button
          onClick={handleSwitch}
          className="z-10 p-3 sm:p-2 absolute top-1/2 -translate-y-1/2 -translate-x-1/2 right-0 rounded-full bg-sleap-purple-300 text-white w-13 h-13 sm:relative sm:-translate-x-0 sm:-translate-y-0 shrink-0 sm:bg-transparent sm:text-sleap-gray-950"
          type="button"
        >
          <ArrowLeftRight />
        </button>
      )}
      <DestinationDropdown
        onChange={handleChangeTo}
        onClick={handleClickTo}
        options={optionsTo}
        value={searchValueTo}
        otherValue={searchValueFrom}
        label={t("HomePageFlights.to")}
        isMultiCity={isMultiCity}
        onClear={onClearTo}
      />
    </div>
  );
};

export default DestinationField;
